import { useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Logo from "@shared/components/Logo";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { toast } from "sonner";
import WaitingForActivationPage from "@/features/auth/pages/WaitingForActivationPage";

/**
 * PrivateRoute component that protects routes requiring authentication
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render when authenticated
 * @param {string[]} props.requiredRoles - Array of roles required to access the route
 * @param {string} props.redirectTo - Custom redirect path (defaults to /login)
 * @param {boolean} props.showError - Whether to show error toast for unauthorized access
 */
export const PrivateRoute = ({
  children,
  requiredRoles = [],
  redirectTo = "/login",
  showError = true,
}) => {
  const auth = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  // Track shown toasts to prevent duplicates - use location key for uniqueness
  const shownToastsRef = useRef(new Set());
  const locationKey = `${location.pathname}${location.search}`;

  // Store the attempted URL for redirect after login
  const from = location.pathname + location.search;

  // Reset toast tracking when location changes
  useEffect(() => {
    shownToastsRef.current.clear();
  }, [locationKey]);

  // Handle authentication errors - only show once per location
  useEffect(() => {
    const errorKey = `error-${locationKey}`;

    if (
      auth.userError &&
      !auth.isLoading &&
      showError &&
      !shownToastsRef.current.has(errorKey)
    ) {
      console.error("Authentication error:", auth.userError);
      toast.error(
        t(
          "auth.sessionExpired",
          "Your session has expired. Please log in again."
        )
      );
      shownToastsRef.current.add(errorKey);
    }
  }, [auth.userError, auth.isLoading, showError, t, locationKey]);

  // Early return for loading state
  if (auth.isLoading) {
    return (
      <div className="h-svh flex items-center justify-center flex-col space-y-4 bg-background">
        <Logo className="w-52 animate-pulse" />
        <ThreeDotLoader />
        <p className="text-sm text-muted-foreground animate-pulse">
          {t("auth.verifying", "Verifying authentication...")}
        </p>
      </div>
    );
  }

  // Handle unauthenticated users
  if (!auth.user) {
    const loginKey = `login-${locationKey}`;
    const shouldShowLoginToast =
      showError &&
      from !== "/" &&
      from !== redirectTo &&
      !shownToastsRef.current.has(loginKey);

    if (shouldShowLoginToast) {
      toast.error(
        t("auth.loginRequired", "Please log in to access this page.")
      );
      shownToastsRef.current.add(loginKey);
    }

    return <Navigate to={redirectTo} state={{ from }} replace />;
  }

  // Handle inactive users early
  if (auth.user.isActive === false) {
    return <WaitingForActivationPage />;
  }

  // Check role-based access
  if (requiredRoles.length > 0 && !auth.hasRole(requiredRoles)) {
    const roleKey = `role-${locationKey}`;

    if (showError && !shownToastsRef.current.has(roleKey)) {
      toast.error(
        t(
          "auth.insufficientPermissions",
          "You don't have permission to access this page."
        )
      );
      shownToastsRef.current.add(roleKey);
    }

    return <Navigate to="/" replace />;
  }

  // User is authenticated, active, and authorized
  return children;
};
